import { CheckCheck, FileCheck2, Hand, Inbox, RefreshCw, RotateCcw, ServerCog, ShieldAlert, UserCheck, UserPlus, UserX } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '@/api';
import { isOpsKind, isRequestKind, isReviewKind, WORK_KINDS, WORK_SEVERITIES, type WorkItem, type WorkKind, type WorkQuery, type WorkSeverity } from '@/api/areas/work';
import { BulkList, useSelection, type BulkAction } from '@/components/BulkList';
import { SavedViews } from '@/components/SavedViews';
import { useAccess } from '@/lib/access';
import { hubUrl } from '@/nav/hubs';
import { Callout, DataTable, FilterBar, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { formatCount } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { SelectField, When } from '../compliance/ui';
import { SeverityBadge, StatusBadge, useWorkSummary } from './shared';

const STATUSES = ['OPEN', 'RESOLVED', 'AUTO_RESOLVED', 'CLOSED'] as const;
const AGES = ['4', '24', '72', '168'] as const;
/** Filters kept in a saved view (the domain / subject scope comes from the page that linked here). */
const VIEW_KEYS = ['status', 'kind', 'severity', 'assignee', 'age', 'category'] as const;

/** `/compliance/work`: everything that needs a person, one item per underlying problem. */
export function WorkQueuePage() {
  const { t } = useTranslation('work');
  const { t: tc } = useTranslation();
  const { t: td } = useTranslation('dossiers');
  const summarize = useWorkSummary();
  useDocumentTitle(t('queue.title'));
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('status') ?? 'OPEN';
  const kind = (params.get('kind') ?? '') as WorkKind | '';
  const severity = (params.get('severity') ?? '') as WorkSeverity | '';
  const assignee = params.get('assignee') ?? '';
  const age = params.get('age') ?? '';
  const domainId = params.get('domain_id') ?? '';
  const subjectId = params.get('subject_id') ?? '';
  // Operations kinds are hidden unless asked for ("Vận hành"), or a kind / domain / subject is chosen.
  const category = (params.get('category') ?? 'work') as 'work' | 'ops' | 'all';
  const effectiveCategory = kind || domainId || subjectId || category === 'all' ? undefined : category;
  const query: WorkQuery = {
    category: effectiveCategory,
    status: (status || undefined) as WorkQuery['status'],
    kind: kind || undefined,
    severity: severity || undefined,
    assignee: assignee || undefined,
    min_age_hours: age ? Number(age) : undefined,
    domain_id: domainId || undefined,
    subject_id: subjectId || undefined,
    limit: 200,
  };
  const result = useApi((signal) => api.listWorkItems(query, { signal }), [status, kind, severity, assignee, age, domainId, subjectId, effectiveCategory]);
  const dossiers = useApi((signal) => api.dossierQueueSummary({ signal }).catch(() => null), []);
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const data = result.status === 'success' ? result.data : undefined;
  const open = data ? Object.values(data.open_by_kind).reduce((n, c) => n + (c ?? 0), 0) : 0;
  const openOps = data ? WORK_KINDS.filter(isOpsKind).reduce((n, k) => n + (data.open_by_kind[k] ?? 0), 0) : 0;
  const openWork = open - openOps;
  const dossierCounts = dossiers.status === 'success' ? dossiers.data : null;
  const urgent = data ? (data.open_by_severity.CRITICAL ?? 0) + (data.open_by_severity.HIGH ?? 0) : 0;

  const { t: tu } = useTranslation('ux');
  const access = useAccess();
  const manage = !access.client && access.has(['work_item.manage']);
  const assignees = useApi((signal) => (manage ? api.listWorkAssignees({ signal }) : Promise.resolve({ items: [] })), [manage]);
  const selection = useSelection<WorkItem>((i) => i.id);
  const itemName = (i: WorkItem) => [t(`kind.${i.kind}`), i.unicode_name].filter(Boolean).join(' · ');
  const closed = (i: WorkItem) => (i.status !== 'OPEN' ? tu('lists.work.closed') : null);
  const actions: BulkAction<WorkItem>[] = manage
    ? [
        {
          id: 'take',
          label: tu('lists.work.take'),
          icon: Hand,
          reason: false,
          skip: (i) => closed(i) ?? (i.assigned_to === access.userId ? tu('lists.work.alreadyYours') : null),
          run: async (i, _in, key) => (await api.assignWorkItem(i.id, {}, { idempotencyKey: key }), tu('lists.work.taken')),
        },
        {
          id: 'assign',
          label: tu('lists.work.assign'),
          icon: UserPlus,
          choice: { label: tu('lists.work.assignee'), options: (assignees.status === 'success' ? assignees.data.items : []).map((a) => ({ value: a.id, label: a.display_name || a.email })) },
          reason: false,
          skip: (i, x) => closed(i) ?? (i.assigned_to === x.choice ? tu('lists.unchanged') : null),
          run: async (i, x, key) => (await api.assignWorkItem(i.id, { assignee_id: x.choice }, { idempotencyKey: key }), tu('lists.work.assigned')),
        },
        {
          id: 'close',
          label: tu('lists.work.close'),
          icon: CheckCheck,
          reason: true,
          // Reviews are decided one by one; requests close when the request is decided.
          skip: (i) => closed(i) ?? (isReviewKind(i.kind) ? tu('lists.work.reviewOne') : isRequestKind(i.kind) ? tu('lists.work.onRequest') : null),
          run: async (i, x, key) => (await api.resolveWorkItem(i.id, { reason: x.reason }, { idempotencyKey: key }), tu('lists.work.done')),
        },
      ]
    : [];
  const rows = result.status === 'success' ? result.data.items : [];

  const columns: Column<WorkItem>[] = [
    ...(access.client ? [] : [selection.column(rows, (i) => tu('bulkList.select', { name: itemName(i) }), tu('bulkList.selectAll'))]),
    { key: 'severity', header: t('queue.col.severity'), cell: (i) => <SeverityBadge severity={i.severity} /> },
    {
      key: 'kind',
      header: t('queue.col.kind'),
      cell: (i) => (
        <Link to={`/compliance/work/${encodeURIComponent(i.id)}`} className="font-medium text-primary hover:underline" onClick={(e) => e.stopPropagation()}>
          {t(`kind.${i.kind}`)}
        </Link>
      ),
    },
    {
      key: 'about',
      header: t('queue.col.subject'),
      cell: (i) => (i.unicode_name ? <span className="font-medium">{i.unicode_name}</span> : <span className="text-muted-foreground">{t('queue.noDomain')}</span>),
    },
    {
      key: 'summary',
      header: t('queue.col.summary'),
      cell: (i) => {
        const f = summarize(i);
        return (
          <span className="line-clamp-2 max-w-xl text-sm" title={f.detail ?? undefined}>
            {f.text ?? <span className="text-muted-foreground">—</span>}
          </span>
        );
      },
    },
    { key: 'opened', header: t('queue.col.opened'), cell: (i) => <When at={i.opened_at} /> },
    {
      key: 'assignee',
      header: t('queue.col.assignee'),
      cell: (i) => (i.assigned_to_display_name ? <span className="text-sm">{i.assigned_to_display_name}</span> : <span className="text-muted-foreground">—</span>),
    },
    { key: 'state', header: t('queue.col.state'), cell: (i) => <StatusBadge item={i} /> },
  ];

  const kindOptions = WORK_KINDS.filter((k) => (effectiveCategory === 'ops' ? isOpsKind(k) : effectiveCategory === 'work' ? !isOpsKind(k) : true)).map((k) => ({ value: k, label: t(`kind.${k}`) }));
  const filtered = Boolean(kind || severity || assignee || age || status !== 'OPEN' || category !== 'work');

  return (
    <>
      <PageHeader
        title={t('queue.title')}
        subtitle={t('queue.subtitle')}
        crumbs={[{ label: t('crumbs.compliance') }]}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        <Callout tone="info">{t('queue.note')}</Callout>
        {effectiveCategory === 'work' && openOps > 0 ? (
          <Callout tone="neutral">
            {td('ops.hidden', { count: openOps })}{' '}
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => set('category', 'ops')}>
              {td('ops.show')}
            </Button>
            {' · '}
            <Link to={hubUrl('ops')} className="text-primary hover:underline">
              {td('ops.openHub')}
            </Link>
          </Callout>
        ) : effectiveCategory === 'ops' ? (
          <Callout tone="neutral">
            {td('ops.showing')}{' '}
            <Button variant="link" size="sm" className="h-auto p-0" onClick={() => set('category', '')}>
              {td('ops.back')}
            </Button>
          </Callout>
        ) : null}
        {domainId || subjectId ? (
          <Callout tone="info">
            {t('queue.filter.scoped')}{' '}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => {
                const p = new URLSearchParams(params);
                p.delete('domain_id');
                p.delete('subject_id');
                setParams(p);
              }}
            >
              {t('queue.filter.clear')}
            </Button>
          </Callout>
        ) : null}
        {data ? (
          <StatGrid>
            {dossierCounts ? (
              <StatCard
                label={td('stats.review')}
                value={formatCount(dossierCounts.needs_review)}
                tone={dossierCounts.needs_review > 0 ? 'serious' : 'ok'}
                icon={FileCheck2}
                hint={`${td('stats.awaiting')}: ${formatCount(dossierCounts.awaiting_customer)} · ${td('stats.dueSoon')}: ${formatCount(dossierCounts.due_soon)}`}
                to={hubUrl('home', 'dossiers')}
                linkLabel={td('title')}
              />
            ) : null}
            <StatCard label={t('queue.stats.open')} value={formatCount(effectiveCategory === 'ops' ? openOps : effectiveCategory === 'work' ? openWork : open)} icon={Inbox} />
            <StatCard label={t('queue.stats.critical')} value={formatCount(urgent)} tone={urgent > 0 ? 'serious' : 'ok'} icon={ShieldAlert} />
            <StatCard label={t('queue.stats.unassigned')} value={formatCount(data.open_unassigned)} tone={data.open_unassigned > 0 ? 'warn' : 'ok'} icon={UserX} />
            <StatCard label={t('queue.stats.mine')} value={formatCount(data.open_mine)} icon={UserCheck} />
            <StatCard label={td('ops.chip')} value={formatCount(openOps)} icon={ServerCog} to={hubUrl('ops')} linkLabel={td('ops.openHub')} />
          </StatGrid>
        ) : null}
        {data && open > 0 ? (
          <Section title={t('queue.byKind')}>
            <div className="flex flex-wrap gap-2">
              {WORK_KINDS.filter((k) => (data.open_by_kind[k] ?? 0) > 0 && (effectiveCategory === 'ops' ? isOpsKind(k) : effectiveCategory === 'work' ? !isOpsKind(k) : true)).map((k) => (
                <Button key={k} size="sm" variant={kind === k ? 'default' : 'outline'} onClick={() => set('kind', kind === k ? '' : k)}>
                  {t(`kind.${k}`)} <span className="tabular ml-1 font-semibold">{formatCount(data.open_by_kind[k] ?? 0)}</span>
                </Button>
              ))}
              {effectiveCategory === 'work' && openOps > 0 ? (
                <Button size="sm" variant="secondary" onClick={() => set('category', 'ops')}>
                  <ServerCog /> {td('ops.chip')} <span className="tabular ml-1 font-semibold">{formatCount(openOps)}</span>
                </Button>
              ) : null}
            </div>
          </Section>
        ) : null}
        <SavedViews list="work" keys={VIEW_KEYS} />
        <BulkList
          selection={selection}
          actions={actions}
          rowKey={(i) => i.id}
          rowLabel={itemName}
          onDone={result.reload}
          csv={{
            name: 'work-items',
            columns: [
              { header: t('queue.col.severity'), value: (i) => t(`severity.${i.severity}`) },
              { header: t('queue.col.kind'), value: (i) => t(`kind.${i.kind}`) },
              { header: t('queue.col.subject'), value: (i) => i.unicode_name },
              { header: t('queue.col.summary'), value: (i) => summarize(i).text ?? i.summary },
              { header: t('queue.col.opened'), value: (i) => i.opened_at },
              { header: t('queue.col.assignee'), value: (i) => i.assigned_to_display_name },
              { header: t('queue.col.state'), value: (i) => t(`status.${i.status}`) },
            ],
          }}
        />
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              filtered ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const p = new URLSearchParams();
                    if (domainId) p.set('domain_id', domainId);
                    if (subjectId) p.set('subject_id', subjectId);
                    setParams(p);
                  }}
                >
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('queue.filter.status')}
              value={status as (typeof STATUSES)[number]}
              options={STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) }))}
              onChange={(v) => set('status', v || 'OPEN')}
              className="w-full sm:w-44"
            />
            <SelectField label={t('queue.filter.kind')} value={kind} allLabel={t('queue.filter.any')} options={kindOptions} onChange={(v) => set('kind', v)} className="w-full sm:w-64" />
            <SelectField
              label={t('queue.filter.severity')}
              value={severity}
              allLabel={t('queue.filter.any')}
              options={WORK_SEVERITIES.map((s) => ({ value: s, label: t(`severity.${s}`) }))}
              onChange={(v) => set('severity', v)}
              className="w-full sm:w-40"
            />
            <SelectField
              label={t('queue.filter.assignee')}
              value={assignee as 'me' | 'unassigned' | ''}
              allLabel={t('queue.filter.any')}
              options={[
                { value: 'me', label: t('queue.filter.me') },
                { value: 'unassigned', label: t('queue.filter.unassigned') },
              ]}
              onChange={(v) => set('assignee', v)}
              className="w-full sm:w-40"
            />
            <SelectField
              label={t('queue.filter.age')}
              value={age as (typeof AGES)[number] | ''}
              allLabel={t('queue.filter.any')}
              options={AGES.map((h) => ({ value: h, label: Number(h) >= 48 ? t('queue.filter.days', { count: Number(h) / 24 }) : t('queue.filter.hours', { count: Number(h) }) }))}
              onChange={(v) => set('age', v)}
              className="w-full sm:w-36"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <Loading rows={5} />
          ) : result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} />
            </div>
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(i) => i.id}
              onRowClick={(i) => navigate(`/compliance/work/${encodeURIComponent(i.id)}`)}
              empty={<EmptyState title={t('queue.empty')} />}
            />
          )}
        </Section>
      </div>
    </>
  );
}
